'use strict';

var SELECTORS = {
    gallery: '.js-plp-tile-gallery',
    slider: '.js-plp-tile-gallery-slick',
    progress: '.js-plp-tile-progress',
    progressActive: '.js-plp-tile-progress-active',
    liveRegion: '.js-plp-tile-live'
};

var isBound = false;

/**
 * Ensures slick arrow buttons have the correct accessibility labels
 * @param {JQuery} $slider - Slick slider element.
 * @returns {void}
 */
function applyArrowA11y($slider) {
    var $gallery = $slider.closest(SELECTORS.gallery);

    // Ensure slider has an id so aria-controls can reference it
    var sliderId = $slider.attr('id');
    if (!sliderId) {
        sliderId = 'plp-tile-slider-' + Math.random().toString(36).slice(2);
        $slider.attr('id', sliderId);
    }

    var $prev = $gallery.find('.slick-prev');
    var $next = $gallery.find('.slick-next');

    if ($prev.length) {
        $prev.attr({
            'aria-label': 'Previous image',
            'aria-controls': sliderId
        });

        if (!$prev.find('.sr-only').length) {
            $prev.append('<span class="sr-only">Previous image</span>');
        }
    }

    if ($next.length) {
        $next.attr({
            'aria-label': 'Next image',
            'aria-controls': sliderId
        });

        if (!$next.find('.sr-only').length) {
            $next.append('<span class="sr-only">Next image</span>');
        }
    }
}

/**
 * Announces the current slide position for screen readers (e.g. "Image 2 of 5").
 * @param {JQuery} $gallery - Gallery element containing the live region.
 * @param {Object} slick - Slick instance.
 * @returns {void}
 */
function announceSlide($gallery, slick) {
    var $live = $gallery.find(SELECTORS.liveRegion);
    if (!$live.length) return;

    var total = slick.slideCount || 1;
    var current = (slick.currentSlide || 0) + 1;

    $live.text('Image ' + current + ' of ' + total);
}

/**
 * Sets the progress indicator segment width once (based on slide count).
 * @param {JQuery} $gallery - Tile gallery root element.
 * @param {Object} slick - Slick instance.
 * @returns {void}
 */
function setProgressWidthOnce($gallery, slick) {
    var $active = $gallery.find(SELECTORS.progressActive);
    if (!$active.length) return;

    var slideCount = slick.slideCount || 1;
    if (slideCount <= 1) return;

    $active.css('width', (100 / slideCount) + '%');
}

/**
 * Updates the progress indicator position using transform only (GPU-friendly).
 * @param {JQuery} $gallery - Tile gallery root element.
 * @param {Object} slick - Slick instance.
 * @returns {void}
 */
function updateProgressTransform($gallery, slick) {
    var $active = $gallery.find(SELECTORS.progressActive);
    if (!$active.length) return;

    var slideCount = slick.slideCount || 1;
    if (slideCount <= 1) return;

    $active.css('transform', 'translate3d(' + (slick.currentSlide * 100) + '%, 0, 0)');
}

/**
 * Initializes slick for a single tile slider, if not already initialized.
 * @param {JQuery} $slider - Slider element inside a tile.
 * @returns {void}
 */
function initTileSlider($slider) {
    if (!$slider.length) return;
    if ($slider.hasClass('slick-initialized')) return;

    var $gallery = $slider.closest(SELECTORS.gallery);
    var $slides = $slider.children();

    if ($slides.length <= 1) {
        $gallery.addClass('is-single-image');
        return;
    }

    $gallery.removeClass('is-single-image');

    if (!$gallery.attr('tabindex')) {
        $gallery.attr('tabindex', '0');
    }

    $slider.off('.plpProgress');

    $slider
        .on('init.plpProgress', function (e, slick) {
            setProgressWidthOnce($gallery, slick);
            updateProgressTransform($gallery, slick);
            applyArrowA11y($slider);
            announceSlide($gallery, slick);
        })
        .on('afterChange.plpProgress', function (e, slick) {
            updateProgressTransform($gallery, slick);
            announceSlide($gallery, slick);
        });

    $slider.removeClass('is-not-initialized');

    $slider.slick({
        slidesToShow: 1,
        slidesToScroll: 1,
        infinite: true,
        arrows: true,
        dots: false,
        swipe: true,
        touchMove: true,
        draggable: true,
        lazyLoad: 'ondemand',
        speed: 220,
        cssEase: 'ease',
        useTransform: false
    });

    var $arrows = $gallery.find('.slick-arrow');
    $arrows.off('.plpMouseFocus')
        .on('pointerdown.plpMouseFocus', function (e) {
            if (e.pointerType === 'mouse') {
                e.preventDefault();
            }
        })
        // Fallback for browsers that don't fully support pointer events
        .on('mousedown.plpMouseFocus', function (e) {
            e.preventDefault();
        });
}

/**
 * Binds delegated event handlers to lazily initialize sliders on user interaction.
 * @returns {void}
 */
function bindOnDemandInit() {
    if (isBound) return;
    isBound = true;

    $(document).on('mouseenter.plpSlick focusin.plpSlick', SELECTORS.slider, function () {
        initTileSlider($(this));
    });

    $(document).on('touchstart.plpSlick pointerdown.plpSlick', SELECTORS.slider, function () {
        initTileSlider($(this));
    });

    $(document).on('keydown.plpSlick', SELECTORS.gallery, function (e) {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

        var $gallery = $(this);
        var $slider = $gallery.find(SELECTORS.slider);
        initTileSlider($slider);

        if (!$slider.hasClass('slick-initialized')) return;

        e.preventDefault();
        $slider.slick(e.key === 'ArrowLeft' ? 'slickPrev' : 'slickNext');
    });
}

/**
 * Initializes any tile sliders already in the DOM to avoid hover layout shifts.
 * @returns {void}
 */
function initExistingTiles() {
    var $sliders = $(SELECTORS.slider);
    if (!$sliders.length) return;

    if (!('IntersectionObserver' in window)) {
        $sliders.each(function () {
            initTileSlider($(this));
        });
        return;
    }

    var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var $slider = $(entry.target);
            initTileSlider($slider);
            observer.unobserve(entry.target);
        });
    }, {
        root: null,
        rootMargin: '200px 0px',
        threshold: 0.01
    });

    $sliders.each(function () {
        observer.observe(this);
    });
}

module.exports = {
    init: function () {
        bindOnDemandInit();
        initExistingTiles();
    }
};
